import React, { useState } from 'react';
import { Badge, Dropdown, Button, Typography, Tooltip, message } from 'antd';
import { BellOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import './NotificationBell.css';

const { Text } = Typography;

const NotificationBell = () => {
    const { t } = useTranslation();
    const { notifications, loading, deleteNotification, markAsRead, unreadCount } = useNotifications();
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleDeleteNotification = async (notificationId, e) => {
        e.stopPropagation(); // Prevent dropdown from closing
        const result = await deleteNotification(notificationId);
        if (result.success) {
            message.success(t('notifications.deleted', { defaultValue: 'Notification deleted' }));
        } else {
            message.error(result.error || t('notifications.deleteError', { defaultValue: 'Failed to delete notification' }));
        }
    };

    const handleMarkAsRead = async (notificationId, e) => {
        e.stopPropagation(); // Prevent dropdown from closing
        const result = await markAsRead(notificationId);
        if (result.success) {
            message.success(t('notifications.markedAsRead', { defaultValue: 'Marked as read' }));
        } else {
            message.error(result.error || t('notifications.markAsReadError', { defaultValue: 'Failed to mark as read' }));
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
        
        if (diffInHours < 1) {
            return t('notifications.justNow', { defaultValue: 'Just now' });
        } else if (diffInHours < 24) {
            return t('notifications.hoursAgo', { hours: diffInHours, defaultValue: `${diffInHours}h ago` });
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            return t('notifications.daysAgo', { days: diffInDays, defaultValue: `${diffInDays}d ago` });
        }
    };

    const notificationItems = notifications.map(notification => ({
        key: notification.id,
        label: (
            <div className="notification-item">
                <div className="notification-content">
                    <div className="notification-header">
                        <Text 
                            strong={!notification.had_been_read}
                            className={`notification-text ${!notification.had_been_read ? 'unread' : ''}`}
                        >
                            {notification.activity}
                        </Text>
                        <div className="notification-actions">
                            {!notification.had_been_read && (
                                <Tooltip title={t('notifications.markAsRead', { defaultValue: 'Mark as read' })}>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        onClick={(e) => handleMarkAsRead(notification.id, e)}
                                        className="notification-action-btn mark-read-btn"
                                    />
                                </Tooltip>
                            )}
                            <Tooltip title={t('notifications.delete', { defaultValue: 'Delete' })}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={(e) => handleDeleteNotification(notification.id, e)}
                                    className="notification-action-btn delete-btn"
                                />
                            </Tooltip>
                        </div>
                    </div>
                    <div className="notification-meta">
                        <Text type="secondary" className="notification-time">
                            {formatDate(notification.created_at)}
                        </Text>
                    </div>
                </div>
            </div>
        ),
    }));

    const dropdownMenu = {
        items: notificationItems.length > 0 ? notificationItems : [{
            key: 'no-notifications',
            label: (
                <div className="no-notifications">
                    <Text type="secondary">
                        {t('notifications.noNotifications', { defaultValue: 'No notifications' })}
                    </Text>
                </div>
            ),
        }],
        className: 'notification-dropdown-menu',
    };

    return (
        <Dropdown
            menu={dropdownMenu}
            placement="bottomRight"
            trigger={['click']}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            overlayClassName="notification-dropdown-overlay"
            disabled={loading}
        >
            <Button
                type="text"
                className="notification-bell-trigger"
                icon={
                    <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                        <BellOutlined className="bell-icon" />
                    </Badge>
                }
            />
        </Dropdown>
    );
};

export default NotificationBell;
